<template>
  <div class="auth-page">
    <!-- Background decoration -->
    <div class="auth-bg">
      <div class="auth-bg-shape shape-1"></div>
      <div class="auth-bg-shape shape-2"></div>
      <div class="auth-bg-shape shape-3"></div>
    </div>

    <div class="auth-container">
      <!-- Left side - Branding -->
      <div class="auth-branding">
        <div class="branding-content">
          <div class="brand-logo">
            <img src="@/assets/img/testpal-full.png" alt="TestPal" class="logo-img" />
          </div>
          <h1 class="brand-title">Secure Your Account</h1>
          <p class="brand-subtitle">Two-factor authentication adds an extra layer of security to your account</p>
          
          <div class="brand-features">
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="shield" size="20" />
              </div>
              <span>Protection against unauthorized access</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="smartphone" size="20" />
              </div>
              <span>Use any authenticator app</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="clock" size="20" />
              </div>
              <span>Time-based one-time passwords</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Setup form -->
      <div class="auth-form-wrapper">
        <div class="auth-form-container">
          <!-- Progress indicator -->
          <div class="step-indicator">
            <div :class="['step', { active: step >= 1, completed: step > 1 }]">
              <span class="step-number">1</span>
              <span class="step-label">Scan</span>
            </div>
            <div class="step-line" :class="{ active: step > 1 }"></div>
            <div :class="['step', { active: step >= 2, completed: step > 2 }]">
              <span class="step-number">2</span>
              <span class="step-label">Verify</span>
            </div>
            <div class="step-line" :class="{ active: step > 2 }"></div>
            <div :class="['step', { active: step >= 3 }]">
              <span class="step-number">3</span>
              <span class="step-label">Done</span>
            </div>
          </div>

          <!-- Step 1: Show QR Code -->
          <Transition name="fade" mode="out-in">
            <div v-if="step === 1" key="step1" class="setup-step">
              <div class="auth-form-header">
                <h2>Scan QR Code</h2>
                <p>Use your authenticator app to scan this code</p>
              </div>

              <div class="qr-section">
                <div class="qr-container">
                  <div v-if="loading" class="qr-loading">
                    <div class="spinner"></div>
                  </div>
                  <img v-else-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code" class="qr-code" />
                </div>
                <p class="qr-hint">
                  <vue-feather type="info" size="14" />
                  Google Authenticator, Authy, or Microsoft Authenticator
                </p>
              </div>

              <div class="manual-entry">
                <div class="divider">
                  <span>or enter manually</span>
                </div>
                <div class="secret-box">
                  <code class="secret-code">{{ secretKey }}</code>
                  <button type="button" class="copy-btn" @click="copySecret" title="Copy to clipboard">
                    <vue-feather type="copy" size="16" />
                  </button>
                </div>
              </div>

              <button class="submit-btn" @click="step = 2">
                <span class="btn-text">Continue</span>
                <vue-feather type="arrow-right" size="18" class="btn-icon" />
              </button>
            </div>

            <!-- Step 2: Verify Code -->
            <div v-else-if="step === 2" key="step2" class="setup-step">
              <div class="auth-form-header">
                <h2>Enter Verification Code</h2>
                <p>Enter the 6-digit code from your authenticator app</p>
              </div>

              <form @submit.prevent="handleVerify" class="verify-form">
                <div class="otp-container">
                  <input
                    v-for="(_, index) in otpDigits"
                    :key="index"
                    :ref="el => otpRefs[index] = el"
                    v-model="otpDigits[index]"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="otp-input"
                    :class="{ 'has-value': otpDigits[index], 'error': error }"
                    @input="handleOtpInput(index)"
                    @keydown="handleOtpKeydown($event, index)"
                    @paste="handlePaste"
                    @focus="error = ''"
                  />
                </div>

                <Transition name="shake">
                  <div v-if="error" class="error-message">
                    <vue-feather type="alert-circle" size="16" />
                    <span>{{ error }}</span>
                  </div>
                </Transition>

                <div class="btn-group">
                  <button type="button" class="back-btn" @click="step = 1">
                    <vue-feather type="arrow-left" size="18" />
                    <span>Back</span>
                  </button>
                  <button 
                    type="submit" 
                    class="submit-btn"
                    :class="{ 'loading': verifying }"
                    :disabled="verifying || !isOtpComplete"
                  >
                    <span v-if="verifying" class="btn-loader"></span>
                    <span class="btn-text">{{ verifying ? 'Verifying...' : 'Verify & Enable' }}</span>
                  </button>
                </div>
              </form>
            </div>

            <!-- Step 3: Success -->
            <div v-else-if="step === 3" key="step3" class="setup-step success-step">
              <div class="success-animation">
                <div class="success-circle">
                  <vue-feather type="check" size="48" />
                </div>
              </div>
              
              <div class="auth-form-header">
                <h2>You're All Set!</h2>
                <p>Two-factor authentication is now enabled on your account</p>
              </div>

              <div class="success-info">
                <div class="info-item">
                  <vue-feather type="shield-off" size="18" />
                  <span>You'll need your authenticator app each time you sign in</span>
                </div>
                <div class="info-item">
                  <vue-feather type="key" size="18" />
                  <span>Keep your recovery codes safe in case you lose access</span>
                </div>
              </div>

              <button class="submit-btn" @click="proceedToDashboard">
                <span class="btn-text">Continue to Dashboard</span>
                <vue-feather type="arrow-right" size="18" class="btn-icon" />
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import QRCode from 'qrcode'

export default {
  name: 'SetupTwoFactor',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const { toast } = useNotifications()

    const step = ref(1)
    const loading = ref(true)
    const verifying = ref(false)
    const error = ref('')
    const qrCodeUrl = ref('')
    const secretKey = ref('')
    const otpDigits = ref(['', '', '', '', '', ''])
    const otpRefs = ref([])

    const isOtpComplete = computed(() => otpDigits.value.every(d => d !== ''))
    const otpCode = computed(() => otpDigits.value.join(''))

    const generateSecret = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
      let secret = ''
      for (let i = 0; i < 16; i++) {
        secret += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      return secret
    }

    const generateQRCodeFromUri = async (totpUri) => {
      try {
        return await QRCode.toDataURL(totpUri, {
          width: 180,
          margin: 2,
          color: { dark: '#003049', light: '#ffffff' }
        })
      } catch (err) {
        console.error('QR Code generation failed:', err)
        return null
      }
    }

    const initSetup = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const result = await authStore.initiate2FASetup()
        secretKey.value = result.secret
        
        if (result.totpUri) {
          qrCodeUrl.value = await generateQRCodeFromUri(result.totpUri)
        } else {
          const email = authStore.user?.email || 'admin@testpal.com'
          const totpUri = `otpauth://totp/TestPal%20Admin:${encodeURIComponent(email)}?secret=${secretKey.value}&issuer=TestPal%20Admin&algorithm=SHA1&digits=6&period=30`
          qrCodeUrl.value = await generateQRCodeFromUri(totpUri)
        }
      } catch (err) {
        console.warn('API failed, generating 2FA locally:', err)
        secretKey.value = generateSecret()
        const email = authStore.user?.email || 'admin@testpal.com'
        const totpUri = `otpauth://totp/TestPal%20Admin:${encodeURIComponent(email)}?secret=${secretKey.value}&issuer=TestPal%20Admin&algorithm=SHA1&digits=6&period=30`
        qrCodeUrl.value = await generateQRCodeFromUri(totpUri)
        authStore.setSetup2FAData({ secret: secretKey.value, totpUri })
      } finally {
        loading.value = false
      }
    }

    const copySecret = () => {
      navigator.clipboard.writeText(secretKey.value)
      toast.success('Secret key copied to clipboard')
    }

    const handleOtpInput = (index) => {
      const value = otpDigits.value[index]
      if (value && index < 5) {
        otpRefs.value[index + 1]?.focus()
      }
    }

    const handleOtpKeydown = (event, index) => {
      if (event.key === 'Backspace' && !otpDigits.value[index] && index > 0) {
        otpRefs.value[index - 1]?.focus()
      }
    }

    const handlePaste = (event) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
      pastedData.split('').forEach((char, i) => {
        if (i < 6) otpDigits.value[i] = char
      })
    }

    const handleVerify = async () => {
      if (!isOtpComplete.value) return

      verifying.value = true
      error.value = ''

      try {
        await authStore.verify2FASetup(otpCode.value, secretKey.value)
        toast.success('Two-factor authentication enabled!')
        step.value = 3
      } catch (err) {
        error.value = typeof err === 'string' ? err : 'Invalid code. Please try again.'
        toast.error('Verification failed')
        otpDigits.value = ['', '', '', '', '', '']
        otpRefs.value[0]?.focus()
      } finally {
        verifying.value = false
      }
    }

    const proceedToDashboard = () => {
      toast.success('Welcome to TestPal Admin!')
      router.push({ name: 'dashboard' })
    }

    onMounted(() => {
      if (!authStore.requiresSetup2FA) {
        router.push({ name: 'login' })
        return
      }
      initSetup()
    })

    return {
      step, loading, verifying, error, qrCodeUrl, secretKey,
      otpDigits, otpRefs, isOtpComplete, copySecret,
      handleOtpInput, handleOtpKeydown, handlePaste,
      handleVerify, proceedToDashboard
    }
  }
}
</script>

<style scoped>
/* Base layout - same as login */
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

.auth-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.auth-bg-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.5;
}

.shape-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.08) 0%, rgba(0, 48, 73, 0.02) 100%);
  top: -200px;
  right: -100px;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, rgba(252, 191, 73, 0.1) 0%, rgba(252, 191, 73, 0.02) 100%);
  bottom: -100px;
  left: -100px;
  animation: float 15s ease-in-out infinite reverse;
}

.shape-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, rgba(142, 202, 230, 0.1) 0%, rgba(142, 202, 230, 0.02) 100%);
  top: 50%;
  left: 30%;
  animation: float 18s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(20px, -20px) rotate(5deg); }
  50% { transform: translate(-10px, 20px) rotate(-5deg); }
  75% { transform: translate(-20px, -10px) rotate(3deg); }
}

.auth-container {
  display: flex;
  width: 100%;
  max-width: 1000px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 20px 25px -5px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Branding section */
.auth-branding {
  flex: 1;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  padding: 48px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.auth-branding::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.branding-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.brand-logo { margin-bottom: 24px; }
.logo-img { height: 48px; }
.brand-title { font-size: 28px; font-weight: 700; margin-bottom: 12px; letter-spacing: -0.02em; }
.brand-subtitle { font-size: 15px; opacity: 0.8; line-height: 1.6; margin-bottom: 40px; }

.brand-features { display: flex; flex-direction: column; gap: 16px; }
.feature-item { display: flex; align-items: center; gap: 12px; font-size: 14px; opacity: 0.9; }
.feature-icon {
  width: 36px; height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Form section */
.auth-form-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.auth-form-container { width: 100%; max-width: 380px; }

/* Step indicator */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.step-number {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #003049;
  color: #fff;
}

.step.completed .step-number {
  background: #10b981;
  color: #fff;
}

.step-label {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step.active .step-label { color: #003049; }

.step-line {
  width: 60px; height: 2px;
  background: #e5e7eb;
  margin: 0 8px 20px;
  transition: background 0.3s ease;
}

.step-line.active { background: #10b981; }

/* Form header */
.auth-form-header { margin-bottom: 28px; text-align: center; }
.auth-form-header h2 { font-size: 24px; font-weight: 700; color: #1f2937; margin-bottom: 8px; letter-spacing: -0.02em; }
.auth-form-header p { color: #6b7280; font-size: 14px; }

/* QR Section */
.qr-section { text-align: center; margin-bottom: 24px; }

.qr-container {
  display: inline-flex;
  padding: 16px;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  margin-bottom: 12px;
}

.qr-code { width: 180px; height: 180px; border-radius: 8px; }

.qr-loading {
  width: 180px; height: 180px;
  display: flex; align-items: center; justify-content: center;
}

.spinner {
  width: 32px; height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #003049;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.qr-hint {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 12px; color: #9ca3af;
}

/* Manual entry */
.manual-entry { margin-bottom: 24px; }

.divider {
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 16px;
}

.divider::before, .divider::after {
  content: ''; flex: 1; height: 1px; background: #e5e7eb;
}

.divider span { font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }

.secret-box {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  background: #f8fafc; padding: 12px 16px; border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.secret-code {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 14px; font-weight: 600;
  letter-spacing: 2px; color: #1f2937;
}

.copy-btn {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: #fff; border: 1px solid #e5e7eb;
  border-radius: 8px; color: #6b7280;
  cursor: pointer; transition: all 0.2s ease;
}

.copy-btn:hover { background: #003049; color: #fff; border-color: #003049; }

/* OTP Input */
.otp-container {
  display: flex; justify-content: center; gap: 10px;
  margin-bottom: 24px;
}

.otp-input {
  width: 48px; height: 56px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 24px; font-weight: 700;
  text-align: center; color: #1f2937;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #003049;
  box-shadow: 0 0 0 4px rgba(0, 48, 73, 0.1);
}

.otp-input.has-value { border-color: #003049; background: #f8fafc; }
.otp-input.error { border-color: #ef4444; animation: shake 0.5s ease; }

/* Error message */
.error-message {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px; background: #fef2f2;
  border: 1px solid #fecaca; border-radius: 10px;
  color: #dc2626; font-size: 14px;
  margin-bottom: 20px;
}

.shake-enter-active { animation: shake 0.5s ease; }

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
}

/* Buttons */
.btn-group { display: flex; gap: 12px; }

.back-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 14px 20px;
  background: #f3f4f6; color: #374151;
  border: none; border-radius: 12px;
  font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.2s ease;
}

.back-btn:hover { background: #e5e7eb; }

.submit-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  flex: 1; padding: 14px 24px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  color: #fff; border: none; border-radius: 12px;
  font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 48, 73, 0.3);
}

.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.submit-btn.loading { pointer-events: none; }

.btn-loader {
  width: 18px; height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.btn-icon { transition: transform 0.2s ease; }
.submit-btn:hover:not(:disabled) .btn-icon { transform: translateX(4px); }

/* Success step */
.success-step { text-align: center; }

.success-animation { margin-bottom: 24px; }

.success-circle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 80px; height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%; color: #fff;
  animation: success-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
}

@keyframes success-pop {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.success-info {
  display: flex; flex-direction: column; gap: 12px;
  margin-bottom: 28px; text-align: left;
}

.info-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  background: #f8fafc; border-radius: 10px;
  font-size: 13px; color: #4b5563;
}

.info-item svg { color: #003049; flex-shrink: 0; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: all 0.3s ease; }
.fade-enter-from { opacity: 0; transform: translateX(20px); }
.fade-leave-to { opacity: 0; transform: translateX(-20px); }

/* Responsive */
@media (max-width: 768px) {
  .auth-container { flex-direction: column; max-width: 440px; }
  .auth-branding { padding: 32px; }
  .brand-title { font-size: 22px; }
  .brand-features { display: none; }
  .auth-form-wrapper { padding: 32px; }
  .otp-input { width: 42px; height: 50px; font-size: 20px; }
}

/* Dark mode */
:global(body.dark-mode) .auth-page { background: linear-gradient(135deg, #111827 0%, #1f2937 100%); }
:global(body.dark-mode) .auth-container { background: #1f2937; }
:global(body.dark-mode) .auth-form-header h2 { color: #f3f4f6; }
:global(body.dark-mode) .auth-form-header p { color: #9ca3af; }
:global(body.dark-mode) .step-number { background: #374151; color: #9ca3af; }
:global(body.dark-mode) .step.active .step-number { background: #3b82f6; }
:global(body.dark-mode) .step-line { background: #374151; }
:global(body.dark-mode) .qr-container { background: #fff; border-color: #374151; }
:global(body.dark-mode) .secret-box { background: #374151; border-color: #4b5563; }
:global(body.dark-mode) .secret-code { color: #f3f4f6; }
:global(body.dark-mode) .otp-input { background: #374151; border-color: #4b5563; color: #f3f4f6; }
:global(body.dark-mode) .otp-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2); }
:global(body.dark-mode) .back-btn { background: #374151; color: #e5e7eb; }
:global(body.dark-mode) .submit-btn { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
:global(body.dark-mode) .info-item { background: #374151; color: #d1d5db; }
</style>
